// artistStyleUtils.js — Artist Type → Artist Style dependent picklist helpers.
//
// Shared by every form that picks an artist's type/style (Add/Edit Artist,
// the AddEvent/EditEvent quick-create modals, AddEvent's paste-lineup form,
// Admin Bulk Edit Artists) and by the event lineup pickers.
//
// Rule (mirrors artistStyleRules.js in createArtist / updateArtist /
// bulkUpdateArtists): a style is valid for type T when
//   style.artist_type_id === T, or
//   style.artist_type_id is null (shared) and T.uses_shared_styles !== false.
// A style requires a type.
//
// Picklist rows come from GET /picklists (or GET /admin/picklists):
//   artist_types:  [{ id, value, uses_shared_styles }]
//   artist_styles: [{ id, value, artist_type_id }]
//
// Ref: SHARC_Spec_Visual_Artists_Image_Credits_2026-10-12.docx §3.1, §5.2

// Referenced by name — Admin Picklists blocks renaming this value.
export const VISUAL_ARTIST_TYPE = 'Visual Artist';

function toId(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n > 0 ? n : null;
}

function findType(types, typeId) {
    const id = toId(typeId);
    if (id === null) return null;
    return (types || []).find(t => t.id === id) || null;
}

// Styles that may be picked for the given type ([] when no type is chosen).
export function stylesForType(styles, types, typeId) {
    const type = findType(types, typeId);
    if (!type) return [];
    const usesShared = type.uses_shared_styles !== false;
    return (styles || []).filter(s =>
        s.artist_type_id === type.id ||
        ((s.artist_type_id === null || s.artist_type_id === undefined) && usesShared)
    );
}

// True when the pair satisfies the rule (no style is always valid).
export function isStyleValidForType(styles, types, typeId, styleId) {
    const sid = toId(styleId);
    if (sid === null) return true;
    return stylesForType(styles, types, typeId).some(s => s.id === sid);
}

// Value to keep in a form's style field after its type changes:
// the current style if still valid, otherwise '' (cleared).
export function styleAfterTypeChange(styles, types, newTypeId, currentStyleId) {
    return isStyleValidForType(styles, types, newTypeId, currentStyleId) ? currentStyleId : '';
}

export function isVisualArtistTypeId(types, typeId) {
    const type = findType(types, typeId);
    return !!type && type.value === VISUAL_ARTIST_TYPE;
}

// For artist rows from GET /artists, which carry the type's display value.
export function isVisualArtist(artist) {
    return !!artist && artist.artist_type === VISUAL_ARTIST_TYPE;
}

// Artist types offered when quick-creating a lineup artist inside an event
// (Visual Artists are never on a lineup — spec D5).
export function lineupArtistTypes(types) {
    return (types || []).filter(t => t.value !== VISUAL_ARTIST_TYPE);
}
